import { throwError } from "@/lib/throwError";
import { get, post } from "@/services/axiosUitils";

/** Normalize file-utility POST response to a flat list of file records. */
export function normalizeFileUtilityUploadResponse(raw: unknown): Record<string, unknown>[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw as Record<string, unknown>[];
  }

  const top = raw as Record<string, unknown>;
  const inner = top.data !== undefined ? top.data : raw;

  if (Array.isArray(inner)) {
    return inner as Record<string, unknown>[];
  }

  const obj = inner as Record<string, unknown> | null;
  if (obj && typeof obj === "object") {
    for (const key of ["data", "results", "files", "records", "items"] as const) {
      const v = obj[key];
      if (Array.isArray(v)) {
        return v as Record<string, unknown>[];
      }
    }
    const hasId = "id" in obj || "pk" in obj;
    const hasPath = "file_path" in obj || "url" in obj;
    if (hasId || hasPath) {
      return [obj];
    }
  }
  return [];
}

class UploadService {
  /**
   * Upload files to file-utility. Returns a **normalized array** of file records (id, file_path, …).
   * Multiple files are uploaded **one request per file** so each response is parsed reliably
   * (many backends only return one record per multipart batch).
   */
  uploadFiles = async ({
    folderName,
    files,
  }: {
    folderName: string;
    files: File[];
  }): Promise<Record<string, unknown>[]> => {
    if (!folderName || !files?.length) {
      throwError("Folder name and files are required", "UploadService");
    }

    const out: Record<string, unknown>[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("folder_name", folderName);
      formData.append("file_path", file);

      const raw = await post("/file-utility/files/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      out.push(...normalizeFileUtilityUploadResponse(raw));
    }

    return out;
  };

  getFileById = async ({ id }: { id: string }): Promise<any> => {
    return await get(`/file-utility/files/${id}/`);
  };
}

export default new UploadService();
